import { useSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import CardHeader from '@mui/material/CardHeader';
import LoadingButton from '@mui/lab/LoadingButton';
import CardContent from '@mui/material/CardContent';

import { FeatureType } from 'src/types/subscription.types';
import {
  EnrichmentRecordType,
  useGetRecordFindingsQuery,
  useAcceptAllSuggestionsMutation,
} from 'src/services/ai/ai-enrichment';

import Iconify from 'src/components/iconify';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FilledByAi, FindingsList } from './findings-list';

type Props = {
  recordType: Extract<EnrichmentRecordType, 'candidate' | 'contact' | 'company'>;
  recordId?: string;
};

/**
 * What the AI found in this record's notes (JT1-1764, JT1-1766): pending suggestions with before
 * and after, proposed tasks, and the fields it filled in by itself. Hidden for agencies without the
 * feature, and when there is nothing to show. Refreshes on its own when a note review finishes
 * (the `ai_note_reviewed` push invalidates the findings).
 */
export default function AiSuggestionsCard({ recordType, recordId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { canAccess } = useFeatures();
  const enabled = canAccess(FeatureType.AI_SOURCING_ENABLED);

  const { currentData } = useGetRecordFindingsQuery({ type: recordType, id: recordId as string }, { skip: !enabled || !recordId });
  const [acceptAll, { isLoading: acceptingAll }] = useAcceptAllSuggestionsMutation();

  const suggestions = currentData?.suggestions ?? [];
  const tasks = currentData?.tasks ?? [];
  const filled = currentData?.filled ?? [];
  if (!enabled || (!suggestions.length && !tasks.length && !filled.length)) return null;

  const onAcceptAll = async () => {
    try {
      const { accepted } = await acceptAll({ type: recordType, id: recordId as string }).unwrap();
      enqueueSnackbar(`${accepted} suggestion${accepted === 1 ? '' : 's'} accepted.`, { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message ?? 'Could not accept the suggestions.', { variant: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader
        title="AI suggestions"
        subheader="Found in notes on this record"
        avatar={<Iconify icon="mdi:robot-outline" width={22} />}
        action={
          suggestions.length > 1 && (
            <LoadingButton size="small" variant="outlined" loading={acceptingAll} onClick={onAcceptAll}>
              Accept all
            </LoadingButton>
          )
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 1 }}>
        <Stack spacing={2} divider={<Divider flexItem />}>
          {(suggestions.length > 0 || tasks.length > 0) && <FindingsList suggestions={suggestions} tasks={tasks} showNoteDate />}
          <FilledByAi filled={filled} />
        </Stack>
      </CardContent>
    </Card>
  );
}
